import { useContext, useCallback, useMemo } from 'react'
import { css } from '@emotion/react'
import DownloadIcon from '@mui/icons-material/GetApp'
import WarningIcon from '@mui/icons-material/Warning'
import Badge from '@mui/material/Badge'
import Fab from '@mui/material/Fab'
import Tooltip from '@mui/material/Tooltip'
import { CvsButton } from '@components'
import { TableToolbar } from '@components/layout/toolbars/tableToolbar'
import { CreateDatasetContext } from '@datasets/reducer/context'
import { colors } from '@global/constants'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { PendingMessage } from './pendingMessage'
import { CreateAISubmission } from '../../create-ai-submission'
import { t } from '@lingui/core/macro'
import { DataContainer } from 'dataContainer/data-container'

interface Props {
  datasetId: string
  tableRef: any
  onSave: () => void
  onSubmit: () => void
  onErrorClick: () => void
  updatedAt: string
  isPending: boolean
  hasDataset: boolean
  submitDisabled: boolean
  saveDisabled: boolean
  binderRef: React.MutableRefObject<null>
  dataContainer: DataContainer
}

const integrationErrorSyle = css`
  padding-bottom: 2px;
`

const integrationErrorIcon = css`
  margin-left: 2px;
  color: ${colors.red.main};
`

const getSubmitTooltipText = ({ requireSubmissionName, integrationErrors, errorCount }) => {
  if (requireSubmissionName) {
    return t`Valid data and a submission name are required`
  }

  if (integrationErrors.length) {
    return integrationErrors.map((err, i) => (
      <div key={i} css={integrationErrorSyle}>
        {err.errorMsg}
      </div>
    ))
  }

  if (errorCount === 0) return ''

  return t`Valid data is required`
}

export const CreateDatasetToolbar = ({
  datasetId,
  tableRef,
  onSave,
  onSubmit,
  onErrorClick,
  isPending,
  submitDisabled,
  saveDisabled,
  binderRef,
  dataContainer,
}: Props) => {
  const { state } = useContext(CreateDatasetContext)
  const { templateId, unsavedChanges, errorCount, errors, isSubmitting, integrationErrors } = state
  const submissionTemplate = useAppSelector(
    state => state.Datasets.submissionTemplatesById[templateId],
  )
  const requireSubmissionName = Boolean(submissionTemplate?.settings?.require_identifier)

  const downloadDraft = useCallback(() => {
    void api
      .get(`/api/v1/account/dataset/${datasetId}/csv/token`)
      .then(({ data }: { data: { token: string } }) => {
        window.open(`/api/v1/account/dataset/${datasetId}/csv/${data.token}`, '_blank')
      })
  }, [datasetId])

  const submitButtonTooltip = useMemo(
    () => getSubmitTooltipText({ requireSubmissionName, integrationErrors, errorCount }),
    [requireSubmissionName, integrationErrors, errorCount],
  )

  const onSearch = e => {
    if (e.key !== 'Enter') return
    // this logic is only going to work with handsontable. Read Table needs it's own search.
    const searchTerm = e.target.value
    const currentSelection = tableRef?.getSelectedLast()
    const startingPoint = currentSelection ? currentSelection[0] : 0
    const startingCol = currentSelection ? currentSelection[1] + 1 : 0
    let exit = false
    const data = tableRef.getData()
    for (let index = startingPoint; index < data.length; index++) {
      for (let colIdx = 0; colIdx < data[index].length; colIdx++) {
        if (index === startingPoint && colIdx < startingCol) {
          // wasted column
        } else if (
          data[index][colIdx]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        ) {
          tableRef.selectCell(index, colIdx)
          tableRef.unlisten()
          exit = true
          break
        }
      }
      if (exit) {
        break
      }
    }

    if (!exit) {
      let secondExit = false
      for (let index = 0; index < startingPoint; index++) {
        for (let colIdx = 0; colIdx < data[index].length; colIdx++) {
          if (index === startingPoint && colIdx < startingCol) {
            // wasted column
          } else if (
            data[index][colIdx]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
          ) {
            tableRef.selectCell(index, colIdx)
            tableRef.unlisten()
            secondExit = true
            break
          }
        }
        if (secondExit) {
          break
        }
      }
    }
    const { target } = e
    e.stopPropagation()
    target.focus()
    setTimeout(() => {
      target.focus()
      target.click()
    }, 200)
    setTimeout(() => {
      target.focus()
      target.click()
    }, 100)
  }

  return (
    <TableToolbar
      template={submissionTemplate}
      errors={errors}
      errorCount={errorCount}
      onErrorClick={onErrorClick}
      onKeyPress={onSearch}
    >
      <PendingMessage isPending={isPending} />

      {datasetId && (
        <Fab
          color="default"
          size="small"
          onClick={downloadDraft}
          disabled={unsavedChanges}
          style={{ boxShadow: 'none' }}
        >
          <DownloadIcon />
        </Fab>
      )}
      <CreateAISubmission
        template={submissionTemplate}
        binderRef={binderRef}
        dataContainer={dataContainer}
      />
      <CvsButton
        name="save"
        size="large"
        variant="contained"
        color="secondary"
        onClick={onSave}
        disabled={saveDisabled}
        style={{ boxShadow: 'none' }}
      >
        {t`Save`}
      </CvsButton>

      <Tooltip title={submitButtonTooltip} enterDelay={300}>
        <div>
          <CvsButton
            name="submit"
            size="large"
            variant="contained"
            color="primary"
            onClick={onSubmit}
            loading={isSubmitting.toString()}
            disabled={submitDisabled}
            style={{ boxShadow: 'none' }}
          >
            {t`Submit`}
          </CvsButton>
          {integrationErrors.length ? (
            <Badge css={integrationErrorIcon}>
              <WarningIcon />
            </Badge>
          ) : null}
        </div>
      </Tooltip>
    </TableToolbar>
  )
}
